/**
 * The KPI modal's drill-down, as in AgencyAnalytics: every integration
 * (those connected to the client bright, the rest greyed) → its metrics,
 * grouped and searchable → the chart to show it as.
 */

import { useMemo, useState, type ReactNode } from 'react';

import type { Catalogue, CatalogueSource } from '../reports/reports-api.ts';
import type { KpiVisual } from './kpis-api.ts';

export const KPI_VISUALS: [KpiVisual, string][] = [
  ['METRIC', 'Stat'],
  ['LINE', 'Line Chart'],
  ['SPARKLINE', 'Sparkline Chart'],
  ['BAR', 'Bar Chart'],
  ['GAUGE', 'Gauge Chart'],
  ['PIE', 'Pie Chart'],
  ['AREA', 'Area Chart'],
  ['HORIZONTAL_BAR', 'Horizontal Bar'],
];

export interface MetricChoice {
  source: string;
  metric: string;
  visual: KpiVisual;
}

function Crumbs(props: {
  source?: CatalogueSource | undefined;
  metric?: string | undefined;
  onBack: (level: 0 | 1) => void;
}): ReactNode {
  return (
    <nav className="kp-crumbs" aria-label="Metric picker">
      <button type="button" className="ex-link" onClick={() => props.onBack(0)}>
        Integrations
      </button>
      {props.source && (
        <>
          <span aria-hidden="true">›</span>
          <button type="button" className="ex-link" onClick={() => props.onBack(1)}>
            {props.source.label}
          </button>
        </>
      )}
      {props.metric && (
        <>
          <span aria-hidden="true">›</span>
          <span>{props.metric}</span>
        </>
      )}
    </nav>
  );
}

export function KpiMetricPicker(props: {
  catalogue: Catalogue;
  /** Sources the client can answer: attached, or not needing an attachment. */
  connected: Set<string>;
  onPick: (choice: MetricChoice) => void;
}): ReactNode {
  const [sourceKey, setSourceKey] = useState<string | null>(null);
  const [metricKey, setMetricKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const source = props.catalogue.sources.find((item) => item.key === sourceKey);
  const metric = source?.metrics.find((item) => item.key === metricKey);
  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const map = new Map<string, NonNullable<typeof source>['metrics']>();
    for (const item of source?.metrics ?? []) {
      if (term && !item.label.toLowerCase().includes(term)) continue;
      const group = item.group ?? source?.label ?? '';
      map.set(group, [...(map.get(group) ?? []), item]);
    }
    return [...map.entries()];
  }, [source, search]);
  const back = (level: 0 | 1) => {
    setMetricKey(null);
    if (level === 0) setSourceKey(null);
  };

  if (!source) {
    const sources = [...props.catalogue.sources].sort(
      (a, b) => Number(props.connected.has(b.key)) - Number(props.connected.has(a.key)),
    );
    return (
      <div className="kp-picker">
        <Crumbs onBack={back} />
        <ul className="kp-pick-list">
          {sources.map((item) => {
            const isConnected = props.connected.has(item.key);
            return (
              <li key={item.key}>
                <button
                  type="button"
                  className={isConnected ? '' : 'kp-greyed'}
                  onClick={() => setSourceKey(item.key)}
                >
                  <strong>{item.label}</strong>
                  <small>
                    {isConnected
                      ? `${item.metrics.length} ${item.metrics.length === 1 ? 'metric' : 'metrics'}`
                      : 'Not connected to this client'}
                  </small>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  if (!metric) {
    return (
      <div className="kp-picker">
        <Crumbs source={source} onBack={back} />
        <label className="ag-search kp-pick-search">
          <span className="sr-only">Search metrics</span>
          <input
            type="search"
            placeholder="Search metrics"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
        </label>
        {groups.map(([group, metrics]) => (
          <section key={group} aria-label={group}>
            <h3 className="kp-pick-group">{group}</h3>
            <ul className="kp-pick-list">
              {metrics.map((item) => (
                <li key={item.key}>
                  <button type="button" onClick={() => setMetricKey(item.key)}>
                    <strong>{item.label}</strong>
                    <small>
                      {source.label} · {item.group ?? 'Metric'}
                    </small>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {groups.length === 0 && <p className="kp-faint">No metric matches “{search}”.</p>}
      </div>
    );
  }

  return (
    <div className="kp-picker">
      <Crumbs source={source} metric={metric.label} onBack={back} />
      <ul className="kp-pick-list kp-pick-visuals">
        {KPI_VISUALS.map(([visual, label]) => (
          <li key={visual}>
            <button
              type="button"
              onClick={() => props.onPick({ source: source.key, metric: metric.key, visual })}
            >
              <strong>{label}</strong>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
